import React, { useEffect, useMemo, useState } from 'react';
import { Employee, InvoiceLine, ProJob, TimeEntry } from '../../../types/pro';
import { formatDate, kr, newLineId } from '../../../utils/pro';
import { Modal } from '../Modal';
interface ImportFromJobDialogProps {
  isOpen: boolean;
  onClose: () => void;
  job: ProJob | undefined;
  timeEntries: TimeEntry[];
  employees: Employee[];
  /** Rate used when an employee has none of their own. */
  fallbackRate: number;
  vatRate: number;
  onImport: (lines: InvoiceLine[]) => void;
}

/**
 * Pulls the real work into the invoice. Hours are grouped per person and work
 * type — a customer wants "12 timer montage", not sixteen daily rows — while
 * materials come across one for one so quantities stay checkable.
 */
export function ImportFromJobDialog({
  isOpen,
  onClose,
  job,
  timeEntries,
  employees,
  fallbackRate,
  vatRate,
  onImport
}: ImportFromJobDialogProps) {
  const rate = typeof job?.hourlyRate === 'number' ? job.hourlyRate : fallbackRate;
  const hourGroups = useMemo(() => {
    if (!job?.id) return [];
    const map = new Map<string, {
      key: string;
      employeeId: string;
      workType: string;
      hours: number;
    }>();
    timeEntries.filter(entry => entry.jobId === job.id && entry.billable).forEach(entry => {
      const key = `${entry.employeeId}|${entry.workType}`;
      const existing = map.get(key);
      if (existing) existing.hours += entry.hours;else map.set(key, {
        key,
        employeeId: entry.employeeId,
        workType: entry.workType,
        hours: entry.hours
      });
    });
    return [...map.values()].sort((a, b) => b.hours - a.hours);
  }, [job?.id, timeEntries]);
  const materials = job?.materials ?? [];
  const hasDescription = Boolean(job?.description?.trim());
  const [pickedHours, setPickedHours] = useState<string[]>([]);
  const [pickedMaterials, setPickedMaterials] = useState<string[]>([]);
  const [includeDescription, setIncludeDescription] = useState(false);

  // Everything is ticked when the dialog opens — importing all of it is the
  // normal case, and unticking the odd line is faster than ticking twelve.
  useEffect(() => {
    if (!isOpen) return;
    setPickedHours(hourGroups.map(group => group.key));
    setPickedMaterials(materials.map(material => material.id));
    setIncludeDescription(false);
  }, [isOpen, job?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (list: string[], setList: (next: string[]) => void, id: string) => setList(list.includes(id) ? list.filter(item => item !== id) : [...list, id]);
  const rateFor = (employeeId: string) => {
    if (typeof job?.hourlyRate === 'number') return job.hourlyRate;
    const employee = employees.find(item => item.id === employeeId);
    return employee?.hourlyRate || fallbackRate;
  };
  const selectedTotal = hourGroups.filter(group => pickedHours.includes(group.key)).reduce((sum, group) => sum + group.hours * rateFor(group.employeeId), 0) + materials.filter(material => pickedMaterials.includes(material.id)).reduce((sum, material) => sum + material.quantity * material.salesPrice, 0);
  const run = () => {
    if (!job) return;
    const lines: InvoiceLine[] = [];
    if (includeDescription && job.description.trim()) {
      lines.push({
        id: newLineId(),
        description: job.description.trim(),
        quantity: 1,
        unit: 'stk',
        unitPrice: 0,
        discount: 0,
        discountType: 'procent',
        vatRate
      });
    }
    hourGroups.filter(group => pickedHours.includes(group.key)).forEach(group => {
      const employee = employees.find(item => item.id === group.employeeId);
      lines.push({
        id: newLineId(),
        description: employee ? `Arbejdsløn ${employee.name} — ${group.workType}` : `Arbejdsløn — ${group.workType}`,
        quantity: Math.round(group.hours * 100) / 100,
        unit: 'timer',
        unitPrice: rateFor(group.employeeId),
        discount: 0,
        discountType: 'procent',
        vatRate
      });
    });
    materials.filter(material => pickedMaterials.includes(material.id)).forEach(material => {
      lines.push({
        id: newLineId(),
        description: material.name,
        quantity: material.quantity,
        unit: 'stk',
        unitPrice: material.salesPrice,
        discount: 0,
        discountType: 'procent',
        vatRate
      });
    });
    onImport(lines);
    onClose();
  };
  const nothingToImport = hourGroups.length === 0 && materials.length === 0 && !hasDescription;
  const row = 'flex items-center gap-3 px-4 py-2.5 transition-colors hover:bg-stone-50';
  const heading = 'border-b border-stone-100 bg-stone-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-stone-500';
  return <Modal isOpen={isOpen} onClose={onClose} title="Hent fra opgave" subtitle={job ? `${job.jobNo} — ${job.title}` : undefined} size="lg" footer={<>
          <span className="mr-auto self-center text-sm text-stone-500" data-id="element-210" data-mp="1rr9zwr7my17t">
            Valgt:{' '}
            <span className="font-semibold tabular-nums text-stone-900" data-id="element-211" data-mp="1wqd4ybnm87fs">
              {kr(selectedTotal)}
            </span>{' '}
            ekskl. moms
          </span>
          <button onClick={onClose} className="rounded-lg border border-stone-200 bg-white px-4 py-2.5 text-sm font-semibold text-stone-700 transition-colors hover:bg-stone-100" data-id="element-212" data-mp="2og7vgbn7m361">
            Annuller
          </button>
          <button onClick={run} disabled={pickedHours.length === 0 && pickedMaterials.length === 0 && !includeDescription} className="rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-700 disabled:bg-stone-200 disabled:text-stone-400" data-id="element-213" data-mp="2fdymvrbi4jva">
            Tilføj valgte til faktura
          </button>
        </>} data-id="element-209" data-mp="20publ8es3amj">
      {nothingToImport ? <p className="py-8 text-center text-sm text-stone-500" data-id="element-214" data-mp="b4dj5i56899q">
          Der er hverken registrerede timer eller materialer på denne opgave
          endnu.
        </p> : <div className="space-y-4" data-id="element-215" data-mp="18tn8vqlxakz">
          {hasDescription && <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-stone-200 p-4" data-id="element-216" data-mp="3sdtr5awog1u6">
              <input type="checkbox" checked={includeDescription} onChange={e => setIncludeDescription(e.target.checked)} className="mt-0.5 h-4 w-4 accent-brand-600" data-id="element-217" data-mp="3obyiflcf2m6d" />
              <span className="min-w-0 text-sm" data-id="element-218" data-mp="1mjaj3n2w8ivv">
                <span className="block font-medium text-stone-900" data-id="element-219" data-mp="1hm0ntqstpd04">
                  Opgavebeskrivelse
                </span>
                <span className="block text-stone-500" data-id="element-220" data-mp="1p59w1gplf5nl">{job?.description}</span>
              </span>
            </label>}

          <section className="overflow-hidden rounded-lg border border-stone-200" data-id="element-221" data-mp="1u2jjwrxc399b">
            <h3 className={heading} data-id="element-222" data-mp="1yxpeef4sxw90">Registrerede timer</h3>
            {hourGroups.length === 0 ? <p className="px-4 py-6 text-center text-sm text-stone-400" data-id="element-223" data-mp="23ws7d17icc0m">
                Ingen fakturerbare timer registreret
              </p> : <ul className="divide-y divide-stone-100" data-id="element-224" data-mp="1b6tf6wy0nyrq">
                {hourGroups.map(group => {
            const employee = employees.find(item => item.id === group.employeeId);
            const price = rateFor(group.employeeId);
            return <li key={group.key} data-id="element-225" data-mp="2rk0frs08g16v">
                      <label className={`${row} cursor-pointer`} data-id="element-226" data-mp="2vllnkyxwtpa7">
                        <input type="checkbox" checked={pickedHours.includes(group.key)} onChange={() => toggle(pickedHours, setPickedHours, group.key)} className="h-4 w-4 accent-brand-600" data-id="element-227" data-mp="2vnyc5uy6axw8" />
                        <span className="min-w-0 flex-1" data-id="element-228" data-mp="3mkxgyikm4urf">
                          <span className="block truncate text-sm font-medium text-stone-900" data-id="element-229" data-mp="47zmusai4w56">
                            {employee?.name ?? 'Medarbejder'}
                          </span>
                          <span className="block text-xs text-stone-500" data-id="element-230" data-mp="e6f3u2ftep0y">
                            {group.workType} · {price.toLocaleString('da-DK')}{' '}
                            kr/time
                          </span>
                        </span>
                        <span className="text-sm tabular-nums text-stone-500" data-id="element-231" data-mp="2fybpi4vp5f9g">
                          {group.hours.toLocaleString('da-DK')} t
                        </span>
                        <span className="w-24 text-right text-sm font-semibold tabular-nums text-stone-900" data-id="element-232" data-mp="qhzdizmz4bj3">
                          {kr(group.hours * price)}
                        </span>
                      </label>
                    </li>;
          })}
              </ul>}
          </section>

          <section className="overflow-hidden rounded-lg border border-stone-200" data-id="element-233" data-mp="lo4vs1qxi6mn">
            <h3 className={heading} data-id="element-234" data-mp="7ovhj7ma9mlb">Materialer</h3>
            {materials.length === 0 ? <p className="px-4 py-6 text-center text-sm text-stone-400" data-id="element-235" data-mp="bstizoz2z9hj">
                Ingen materialer på opgaven
              </p> : <ul className="divide-y divide-stone-100" data-id="element-236" data-mp="2r3no822vqeh">
                {materials.map(material => <li key={material.id} data-id="element-237" data-mp="1s5s20eu8bif5">
                    <label className={`${row} cursor-pointer`} data-id="element-238" data-mp="1s6tbwfzidyva">
                      <input type="checkbox" checked={pickedMaterials.includes(material.id)} onChange={() => toggle(pickedMaterials, setPickedMaterials, material.id)} className="h-4 w-4 accent-brand-600" data-id="element-239" data-mp="1x5mqycug1xxk" />
                      <span className="min-w-0 flex-1" data-id="element-240" data-mp="37hf86yauyzcr">
                        <span className="block truncate text-sm font-medium text-stone-900" data-id="element-241" data-mp="37iqdx3nv6h6i">
                          {material.name}
                        </span>
                        <span className="block font-mono text-xs text-stone-400" data-id="element-242" data-mp="2xipqb5iypewi">
                          {material.itemNo}
                        </span>
                      </span>
                      <span className="text-sm tabular-nums text-stone-500" data-id="element-243" data-mp="1m4cfp2d7yvyf">
                        {material.quantity.toLocaleString('da-DK')} stk
                      </span>
                      <span className="w-24 text-right text-sm font-semibold tabular-nums text-stone-900" data-id="element-244" data-mp="1jjxnk6n18drd">
                        {kr(material.quantity * material.salesPrice)}
                      </span>
                    </label>
                  </li>)}
              </ul>}
          </section>

          {job?.start && <p className="text-xs text-stone-400" data-id="element-245" data-mp="1nmaqyoeb3zkz">
              Opgaven blev planlagt til {formatDate(job.start)}.
            </p>}
        </div>}
    </Modal>;
}